
import HeaderDropshipping from '@marketplace/components/core/HeaderDropshipping.vue'
import NotificationCenter from '@marketplace/components/NotificationCenter.vue'
import PulseTooltipWrapper from '@marketplace/components/pulse/pulse-tooltip-wrapper.vue'
import { mapGetters } from 'vuex'

export default {
    name: 'DropshippingPagesLayout',

    components: {
        HeaderDropshipping,
        PulseTooltipWrapper,
        NotificationCenter,
    },

    props: {
        hasCustomScroll: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            // Check if the browser supports CSS aspect-ratio
            supportsAspectRatio: CSS.supports('aspect-ratio', '1 / 1'),
        }
    },

    head() {
        return {
            htmlAttrs: {
                class: 'lg:overflow-hidden',
                meta: [{ name: 'theme-color', content: '#42594F' }],
            },
        }
    },

    computed: {
        ...mapGetters('global', [
            'isSupplier',
            'inboxUnreadCount',
            'account',
            'hasActiveSubscription',
            'hasActiveIntegration',
        ]),
        ...mapGetters('dropshipping', ['unreadDropshippingNotificationsCount']),
        ...mapGetters('feature', ['hasOneBrandFeature']),
        ...mapGetters('ds', ['dsStorefronts']),

        sideMenuLinks() {
            if (this.$helpers.isSupplierAccount) {
                return [
                    {
                        title: this.$t('nav.ds.section.explore'),
                        items: [
                            {
                                label: this.$t('nav.ds.find_brands'),
                                to: this.localePath({ name: 'ds-marketplace' }),
                                icon: 'far fa-store',
                            },
                        ],
                    },
                ]
            }

            const sideMenu = []

            const hasStorefronts =
                Array.isArray(this.dsStorefronts) &&
                this.dsStorefronts.length > 0

            if (
                (this.hasOneBrandFeature && hasStorefronts) ||
                !this.hasOneBrandFeature
            ) {
                const items =
                    this.hasOneBrandFeature && hasStorefronts
                        ? this.dsStorefronts.map((sf) => ({
                              label: sf.storefront?.name,
                              to: this.localePath({
                                  name: 'ds-store-slug',
                                  params: { slug: sf.storefront?.slug },
                              }),
                              icon: 'far fa-store',
                          }))
                        : [
                              {
                                  label: this.$t('nav.ds.find_brands'),
                                  to: this.localePath({
                                      name: 'ds-marketplace',
                                  }),
                                  icon: 'far fa-store',
                              },
                          ]

                sideMenu.push({
                    title: this.$t('nav.ds.section.explore'),
                    items,
                })
            }

            sideMenu.push(
                {
                    title: this.$t('nav.ds.section.orders'),
                    items: [
                        {
                            label: this.$t('nav.ds.orders'),
                            to: this.localePath({ name: 'ds-orders' }),
                            icon: 'far fa-archive',
                        },
                    ],
                },
                {
                    title: this.$t('nav.ds.section.catalog'),
                    items: [
                        {
                            label: this.$t('nav.ds.import_list'),
                            to: this.localePath({ name: 'ds-import-list' }),
                            icon: 'far fa-download',
                            hasTooltip: true,
                        },
                        {
                            label: this.$t('nav.ds.connected_brands'),
                            to: this.localePath({ name: 'ds-brands' }),
                            icon: 'far fa-store-alt',
                        },
                        {
                            label: this.$t('nav.ds.product_updates'),
                            to: this.localePath({ name: 'ds-product-updates' }),
                            icon: 'far fa-sync-alt',
                            id: 'ds_product_updates',
                            notificationCount:
                                this.unreadDropshippingNotificationsCount,
                        },
                    ],
                },
                {
                    title: this.$t('nav.ds.section.other'),
                    items: [
                        {
                            label: this.$t('nav.ds.inbox'),
                            to: this.localePath({ name: 'ds-inbox' }),
                            icon: 'far fa-envelope',
                            notificationCount: this.inboxUnreadCount,
                        },
                        {
                            label: this.$t('nav.ds.subscription'),
                            to: this.localePath({ name: 'ds-subscription' }),
                            icon: 'far fa-credit-card',
                        },
                        {
                            label: this.$t('nav.ds.settings'),
                            to: this.localePath({ name: 'ds-settings' }),
                            icon: 'far fa-cog',
                        },
                    ],
                }
            )

            return sideMenu
        },

        mainBgColor() {
            if (
                (this.$route.name.startsWith('ds') &&
                    !this.$route.name.startsWith('ds-')) ||
                this.$route.name.startsWith('ds-listings') ||
                this.$route.name.startsWith('ds-store') ||
                this.$route.name.startsWith('ds-marketplace') ||
                this.$route.name.startsWith('ds-search')
            ) {
                return 'bg-white'
            }

            return 'bg-grey-300'
        },

        showSubscriptionNotification() {
            const excludesPages = ['ds-subscription', 'ds-inbox']
            const isExcludedPage = excludesPages.some((page) =>
                this.$route.name.startsWith(page)
            )

            return (
                !this.hasActiveSubscription &&
                !isExcludedPage &&
                !this.isSupplier
            )
        },

        showIntegrationNotification() {
            const excludesPages = ['ds-inbox']
            const isExcludedPage = excludesPages.some((page) =>
                this.$route.name.startsWith(page)
            )

            // Early return for excluded pages or suppliers
            if (isExcludedPage || this.isSupplier) {
                return false
            }

            // Check if any channel is 'disabled' or 'disabled_externally'
            const hasDisabledChannels = this.account.channels.edges?.some(
                (edge) =>
                    ['disabled', 'disabled_externally'].includes(
                        edge.node.status
                    )
            )

            const activeSubWithEmptyChannel =
                this.hasActiveSubscription &&
                this.account.channels.edges.length === 0

            return (
                (hasDisabledChannels && !this.hasActiveIntegration) ||
                activeSubWithEmptyChannel
            )
        },
    },
}
